<template>
  <v-dialog
    v-if="internalValue"
    class="add-test-popup"
    v-model="internalValue"
    :width="380"
    @click:outside="cancel"
    @keydown.esc="cancel"
  >
    <v-card class="popup-wrapper">
      <div class='popup-wrapper__action'>
        <span class='close_btn' @click='cancel'>
          <span class="material-icons-outlined">
            close
          </span>
        </span>
      </div>
      <div class='popup-header'>
        <div class="popup-title">
          {{ $t('configuration.add.test') }}
        </div>
      </div>
      <add-test-form
        v-model="test"
        :website="website"
        :tab="tab"
        :testTypes="testTypes"
        :portsToSuggest='portsToSuggest'
        :enableCloudComplianceFeature='enableCloudComplianceFeature'
        :has-license='hasLicense'
        :noDiscoveryFinishedForTLD="noDiscoveryFinishedForTLD"
        :isDomainValidated="isDomainValidated"
        class="add-test-form"
        @switchSubmitButtonLabel='switchSubmitButtonLabel'
        @cancel='cancel'
        @showFeatureTogglePopup='showFeatureTogglePopup'
      />
      <div class="actions d-flex">
        <v-spacer />
        <v-btn class="btn-tertiary" @click="cancel">
          {{ $t('cancel') }}
        </v-btn>

        <v-btn :disabled="isDisabled" class="btn-primary" :loading="submitting" @click="submit">
          {{ submitLabel }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';
import {
  DATA_LEAKS_TYPES,
  TEST_REPEAT_STATUS,
  TEST_TYPES_HOSTS,
  TEST_TYPES_SUBDOMAINS,
  TEST_TYPES_TLDS,
} from '@/constants/projects';
import { getGroupedTimezonesList } from '@/utils/dateUtils';
import AddTestForm from '@/components/forms/AddTestForm.vue';
import { LOAD_CREDENTIALS_FROM_CONFIGURATION } from '@/store/project/actionTypes';
import { mapGetters } from 'vuex';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

const getTimeWithHourOffset = offset => moment()
  .startOf('hour')
  .add(offset, 'hour')
  .format('HH:mm');

const getTimeWithMinuteOffset = offset => moment()
  .startOf('minute')
  .add(offset, 'minute')
  .format('HH:mm');

const defaultTest = {
  type: '',
  schedule: false,
  isAllDay: false,
  startDate: moment().format(),
  startTime: getTimeWithMinuteOffset(2),
  endTime: getTimeWithHourOffset(2),
  timezone: String(moment().utcOffset()),
  customRepeatData: {},
  frequency: TEST_REPEAT_STATUS.never,
  metaData: {
    advancedOptions: {
      specifyPath: false,
      protocol: '',
      path: '',
      port: '',
    },
    hostForProxyVuln: '',
    proxySettings: {
      useProxy: false,
      address: {
        ip: '',
        port: '1080',
      },
      credentials: {
        userName: '',
        password: '',
      },
    },
    authentication: {
      useAuthentication: false,
      authenticationObject: {},
    },
    sensitive: false,
    subdomain: '',
    generateReport: false,
  },
  valid: true,
};

export default {
  name: 'AddTestPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  components: {
    AddTestForm,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    portsToSuggest: {
      type: Array,
      required: false,
      default: () => [],
    },
    website: {},
    tab: {
      type: String,
      default: '',
      required: false,
    },
    tld: {
      type: String,
      required: false,
      default: '',
    },
    testTypes: {},
    item: {},
    organization: {},
    hostList: {
      type: Array,
      required: false,
      default: () => [],
    },
    enableCloudComplianceFeature: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      submitLabel: this.$t('test.scan.now'),
      test: {
        ...defaultTest,
        startDate: moment().format(),
        startTime: getTimeWithMinuteOffset(2),
        dataLeakType: DATA_LEAKS_TYPES.all,
        hostOptionList: this.hostList,
        selectedHosts: [],
        metaData: {
          advancedOptions: {
            specifyPath: false,
            protocol: '',
            path: '',
            port: '',
          },
          hostForProxyVuln: '',
          proxySettings: {
            useProxy: false,
            address: {
              ip: '',
              port: '1080',
            },
            credentials: {
              userName: '',
              password: '',
            },
          },
          authentication: {
            useAuthentication: false,
            authenticationObject: {},
          },
          sensitive: false,
          subdomain: '',
          generateReport: false,
        },
        valid: true,
      },
      submitting: false,
    };
  },
  computed: {
    ...mapGetters(['licenses', 'hasDiscoveryFinished', 'companyData', 'configurationCloudAccounts', 'hasLicenseModule']),
    hasLicense() {
      const organizationName = this.organization?.name || this.companyData.name;
      return !!this.licenses[organizationName]?.length;
    },
    formattedTimeZone() {
      return getGroupedTimezonesList().map(tz => ({
        value: tz.offset,
        name: tz.name,
        id: tz.id,
      }));
    },
    isDisabled() {
      return !this.test.valid ||
        (this.test.schedule && !this.hasLicense) ||
        !this.test.type ||
        (Object.values(TEST_TYPES_HOSTS).includes(this.test.type) && !this.test.selectedHosts.length) ||
        (this.test.type === TEST_TYPES_SUBDOMAINS.vuln_infra && this.test.metaData.proxySettings.useProxy && !this.test.metaData.hostForProxyVuln.length) ||
        (this.test.type === this.$t('test.testType.placeholder') && !this.enableCloudComplianceFeature) ||
        (this.disableNoDiscovery) ||
        (this.disableNoDomainValidation);
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    disableNoDiscovery() {
      return (this.test.type === TEST_TYPES_TLDS.dataleaks || this.test.type === TEST_TYPES_TLDS.sitemap) && this.noDiscoveryFinishedForTLD;
    },
    disableNoDomainValidation() {
      return (this.test.type === TEST_TYPES_SUBDOMAINS.vuln_infra || this.test.type === TEST_TYPES_HOSTS.deepscan || this.test.type === TEST_TYPES_HOSTS.autoexplotation) && !this.isDomainValidated;
    },
    noDiscoveryFinishedForTLD() {
      return !(Object.keys(this.hasDiscoveryFinished).includes(this.website) && this.hasDiscoveryFinished[this.website].hasDiscoveryFinished);
    },
    isDomainValidated() {
      if (this.hasLicenseModule('api')) return true;
      const isCloudAccount = this.configurationCloudAccounts.some(item => item.configData === this.tld);
      return (Object.keys(this.hasDiscoveryFinished).includes(this.tld) && this.hasDiscoveryFinished[this.tld].validated) || isCloudAccount;
    },
  },
  watch: {
    internalValue(value) {
      if (value) {
        this.init();
      }
    },
    'test.isAllDay': {
      handler(value) {
        this.test.startTime = value ? null : getTimeWithMinuteOffset(2);
        this.test.endTime = value ? null : getTimeWithHourOffset(2);
      },
      deep: true,
    },
  },
  methods: {
    switchSubmitButtonLabel(scheduleValue) {
      if (scheduleValue) {
        this.submitLabel = this.$t('save');
      } else {
        this.submitLabel = this.$t('test.scan.now');
      }
    },
    async init() {
      this.test = {
        ...defaultTest,
        startDate: moment().format(),
        startTime: getTimeWithMinuteOffset(2),
        dataLeakType: DATA_LEAKS_TYPES.all,
        hostOptionList: this.hostList,
        selectedHosts: [],
        metaData: {
          advancedOptions: {
            specifyPath: false,
            protocol: '',
            path: '',
            port: '',
          },
          hostForProxyVuln: '',
          proxySettings: {
            useProxy: false,
            address: {
              ip: '',
              port: '1080',
            },
            credentials: {
              userName: '',
              password: '',
            },
          },
          authentication: {
            useAuthentication: false,
            authenticationObject: {},
          },
          sensitive: false,
          subdomain: '',
          generateReport: false,
        },
        valid: true,
      };
      this.submitting = false;
      await this.$store.dispatch(LOAD_CREDENTIALS_FROM_CONFIGURATION);
    },
    cancel() {
      this.internalValue = false;
    },
    showFeatureTogglePopup() {
      this.$emit('showFeatureTogglePopup');
    },
    submit() {
      if (this.test.valid) {
        if (!this.hasLicense && this.test.schedule) {
          this.test.schedule = false;
        }
        if (this.test.isAllDay) {
          this.test.startTime = getTimeWithHourOffset(1);
          this.test.endTime = getTimeWithHourOffset(2);
        }
        if (this.test.metaData.advancedOptions.specifyPath) {
          if (!this.test.metaData.advancedOptions.port.length) {
            if (this.test.metaData.advancedOptions.protocol.toLowerCase() === this.$t('test.select.protocol.https').toLowerCase()) {
              this.test.metaData.advancedOptions.port = '443';
            } else {
              this.test.metaData.advancedOptions.port = '80';
            }
          }
        }
        if (this.test.type === TEST_TYPES_HOSTS.deepscan) {
          this.test.metaData.subdomain = this.website;
        }
        const selectedTimeZone = this.formattedTimeZone.find(timezone => timezone.value === this.test.timezone);
        this.test.timezone = selectedTimeZone.id;
        if (this.test.selectedHosts.length) {
          for (let i = 0; i < this.test.selectedHosts.length; i += 1) {
            const currentHost = this.test.selectedHosts[i];
            this.test = {
              ...this.test, url: currentHost, tld: this.tld,
            };
            this.$emit('submit', this.test);
          }
        } else {
          this.test = {
            ...this.test, url: this.website.configData || this.website, tld: this.tld,
          };
          this.$emit('submit', this.test);
        }
        this.internalValue = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.popup-wrapper {
  padding: $space-6 $space-10 $space-10 $space-10;
  &__action {
    .close_btn {
      margin-right: rem(-20);
      margin-top: rem(-5);
      float: right;

      &:hover {
        cursor: pointer;
        color: var(--brand-primary);
      }
    }
  }
  .popup-header {
    display: flex;
    justify-content: space-between;
    .popup-title {
      font-size: $font-size-base;
      text-transform: uppercase;
      color: var(--text-primary);
      font-weight: 600;
    }
    .popup-icon {
      .v-icon{
        font-size: $font-size-xl;
        color: var(--text-secondary);
      }
    }
  }

  .add-test-form {
    margin-top: $space-5;
  }

  .actions {
    margin-top: $space-8;
  }

  .actions .v-btn {
    margin-left: $space-2;
    text-transform: uppercase !important;
    letter-spacing: 1.07px;
    font-weight: 500;
  }

  .actions .v-btn.primary {
    background-color: var(--brand-primary) !important;
    color: #ffffff !important;

    &.v-btn--disabled {
      background-color: var(--bg-tertiary) !important;
      color: var(--text-muted) !important;
    }
  }

  .actions .v-btn.v-btn--outlined.primary--text {
    border-color: var(--brand-primary) !important;
    color: var(--brand-primary) !important;
    background-color: transparent !important;
  }

  .actions .btn-tertiary {
    border: 1px solid var(--brand-primary) !important;
    color: var(--brand-primary) !important;
    background-color: var(--bg-primary) !important;
  }

  .actions .btn-primary {
    background-color: var(--brand-primary) !important;
    color: var(--text-inverse) !important;
  }
}

</style>

<style lang="scss">
// Override gray background on select input inside add-test popup
.popup-wrapper .add-test-form .el-input__inner {
  background-color: transparent !important;
}
</style>
